import area from "@turf/area";
import buffer from "@turf/buffer";
import { multiPolygon, point, polygon } from "@turf/helpers";
import polygonToLine, { multiPolygonToLine } from "@turf/polygon-to-line";
import type {
  Feature,
  GeoJsonProperties,
  Feature as GeojsonFeature,
  Geometry,
  GeometryCollection,
  MultiPolygon,
  Polygon,
  Position,
} from "geojson";

import { snakeToCamelCase, uuidv4 } from "@/features/utils/utils";
import { addZCoordinate } from "../geo";
import type IMDFTheme from "./theme";
import { HIGHLIGHT_COLOR } from "./theme";

export const geometriesToExtrude = ["Polygon", "MultiPolygon"];

export type SimpleGeometry = Exclude<Geometry, GeometryCollection<Geometry>>;

class IMDFFeature<
  G extends SimpleGeometry | null = SimpleGeometry,
  P = GeoJsonProperties,
> implements GeojsonFeature<G, P>
{
  type: "Feature";
  id: string;
  properties: P;
  geometry: G;
  feature_type: string;

  constructor({
    type,
    id,
    feature_type = "",
    properties,
    geometry,
  }: {
    type: string;
    id: string;
    geometry: G;
    properties?: P;
    feature_type?: string;
  }) {
    //@ts-expect-error
    this.type = type;
    this.id = id;
    this.feature_type = feature_type;
    this.properties = (properties ?? {}) as P;
    this.geometry = geometry;
  }

  static fromCoords(c: Coordinate3D[]) {
    return new IMDFFeature({
      geometry: { coordinates: [c], type: "Polygon" },
      id: uuidv4(),
      type: "Feature",
    });
  }

  public getGeoJSONFeature(): GeojsonFeature {
    return {
      //@ts-expect-error
      feature_type: this.feature_type,
      geometry: this.geometry,
      id: this.id,
      properties: this.properties,
      type: "Feature",
    };
  }

  private toTurfPolygon() {
    if (this.geometry.type == "MultiPolygon") {
      return multiPolygon(this.geometry.coordinates);
    } else if (this.geometry.type == "Point") {
      return point(this.geometry.coordinates);
    } else if (this.geometry.type == "Polygon") {
      return polygon(this.geometry.coordinates);
    }
  }

  public toTurfLine() {
    if (this.geometry.type == "MultiPolygon") {
      return multiPolygonToLine(this.geometry);
    } else if (this.geometry.type == "Polygon") {
      return polygonToLine(this.geometry);
    }
  }

  /**
   * To prevent z-fighting, this function calculates a minimal height between
   * 0 and 0.1 proportional to the area of the feature. This way, features
   * with smaller sizes will be painted on top of large features, while
   * preventing z-fighting.
   */
  public getSafeZ = () => {
    let a: number;
    const MAX_OFFSET: number = 0.001;

    try {
      a = area(this.toTurfPolygon());
    } catch (_e) {
      a = 0;
    }
    const areaBasedOffset = MAX_OFFSET / (1 + a);
    return areaBasedOffset;
  };

  /**
   * Decides if a feature should be extruded based on its type and extrusion height
   * @param theme the theme to use to get the extrusion height
   * @param extrudePolygons whether to extrude polygons or not
   * @returns true if the feature should be extruded, false otherwise
   */
  public shouldExtrude = (theme: IMDFTheme, extrudePolygons = true) => {
    const extrusionHeight = theme.getExtrusionHeight(this);
    return (
      geometriesToExtrude.includes(this.geometry.type) &&
      extrusionHeight > 0 &&
      extrudePolygons
    );
  };

  public getFillColor = (
    theme: IMDFTheme,
    options: {
      forceTranslucent: boolean;
      enableSuperHighlight: boolean;
    },
  ) => {
    return theme
      .getFillColor(this, {
        enableSuperHighlight:
          options.enableSuperHighlight && !options.forceTranslucent,
      })
      .hex();
  };

  public getFillColorWithOpacity = (
    theme: IMDFTheme,
    extruded: boolean,
    options: {
      geojsonOpacity: number;
      extrusionOpacity: number;
      polygonOpacity: number;
      forceTranslucent: boolean;
      enableSuperHighlight: boolean;
    },
  ) => {
    const color = theme
      .getFillColor(this, {
        enableSuperHighlight:
          options.enableSuperHighlight && !options.forceTranslucent,
      })
      .array();
    const opacity = this.getFillOpacity(theme, extruded, {
      ...options,
      normalized: false,
    });

    color && (color[3] = opacity);
    return color;
  };

  public getFillOpacity = (
    theme: IMDFTheme,
    extruded: boolean,
    options: {
      geojsonOpacity: number;
      extrusionOpacity: number;
      polygonOpacity: number;
      forceTranslucent: boolean;
      normalized: boolean;
    },
  ) => {
    const opacity = theme.getFillOpacity(this, {
      forceTranslucent: options.forceTranslucent,
    });
    const globalOpacity =
      options.geojsonOpacity * opacity * (options.normalized ? 1.0 : 255.0);
    return extruded
      ? globalOpacity * (options.extrusionOpacity / 255)
      : globalOpacity * (options.polygonOpacity / 255);
  };

  public getStrokeOpacity = (
    theme: IMDFTheme,
    options: {
      geojsonOpacity: number;
      forceTranslucent: boolean;
      normalized: boolean;
    },
  ) => {
    const opacity = theme.getStrokeOpacity(this, {
      forceTranslucent: options.forceTranslucent,
    });
    return (
      options.geojsonOpacity * opacity * (options.normalized ? 1.0 : 255.0)
    );
  };

  public getLineColor = (theme: IMDFTheme) => {
    return theme.getStrokeColor(this).hex();
  };

  public getStrokeWidth = (theme: IMDFTheme) => {
    return theme.getStrokeWidth(this);
  };

  public getLineColorWithOpacity = (
    theme: IMDFTheme,
    options: {
      wireframeOpacity: number;
      forceTranslucent: boolean;
      enableSuperHighlight: boolean;
    },
  ) => {
    const color = options.enableSuperHighlight
      ? HIGHLIGHT_COLOR.array()
      : theme.getStrokeColor(this).array();
    const opacity =
      options.wireframeOpacity *
      (theme.getStrokeWidth(this) > 0
        ? theme.getStrokeOpacity(this, options)
        : 0);
    color && (color[3] = opacity);

    return color;
  };

  public getSafeExtrusionHeight = (
    theme: IMDFTheme,
    options?: {
      enableSuperHighlight?: boolean;
    },
  ) => {
    let a: number;
    const MAX_OFFSET: number = 0.001;

    try {
      a = area(this.toTurfPolygon());
    } catch (_e) {
      a = 0;
    }
    const areaBasedOffset = MAX_OFFSET / (1 + a);
    return theme.getExtrusionHeight(this, options) + areaBasedOffset;
  };

  /**
   * Given a feature and a margin, returns a new feature scaled by the given margin.
   * The scaling is done by calling `scalePolygon` or `scaleMultiPolygon` depending
   * on the geometry type of the feature.
   *
   * @param margin The margin to be added to the feature
   * @returns The scaled feature
   */
  public scale = (margin: number): IMDFFeature<G, P> => {
    if (this.geometry.type === "Polygon") {
      return this.scalePolygon(margin);
    } else if (this.geometry.type === "MultiPolygon") {
      return this.scaleMultiPolygon(margin);
    }
    return new IMDFFeature({ ...this });
  };

  private scalePolygon = (margin: number): any => {
    const scaledPoly = this.calculateValidBuffer(
      this.geometry.coordinates as Position[][],
      margin,
    );
    return new IMDFFeature({ ...this, geometry: scaledPoly.geometry });
  };

  /**
   * Calculate a valid buffer from a set of coordinates, by scaling the
   * original polygon by the given amount until it is valid.
   *
   * @param amount The amount to scale the polygon by.
   * @return A valid buffer polygon.
   */
  private calculateValidBuffer = (
    coordinates: Position[][] | Position[][][],
    amount: number,
  ): GeojsonFeature<Polygon | MultiPolygon> => {
    if (coordinates.length === 0) {
      return polygon([]);
    }
    // @ts-expect-error
    const scaledPoly = buffer(polygon(coordinates), amount, {
      units: "meters",
    });
    if (scaledPoly) return scaledPoly;
    return this.calculateValidBuffer(coordinates, amount + 0.1);
  };

  private scaleMultiPolygon = (margin: number): any => {
    const scaledPolyArray = (this.geometry.coordinates as Position[][][]).map(
      (coordinates) => this.calculateValidBuffer(coordinates, margin),
    );

    return new IMDFFeature({
      ...this,
      geometry: {
        coordinates: scaledPolyArray.map(
          (polygon) => polygon.geometry.coordinates,
        ),
        type: "MultiPolygon",
      },
    });
  };

  /**
   * This function adds a minimal height to the coordinates of a feature in order
   * to prevent z-fighting between features with the same height.
   *
   * @param f the feature to add height to
   * @returns a new feature with the safe height
   */
  public handleZFighting = () => {
    if (
      !this.geometry?.coordinates ||
      !this.geometry.coordinates.some((c) => c == undefined || c == null)
    ) {
      return;
    }

    const coordinates = addZCoordinate(this.getCoordinates(), this.getSafeZ());

    this.setCoordinates(coordinates);
  };

  public getCoordinates():
    | Coordinate3D
    | Coordinate3D[]
    | Coordinate3D[][]
    | Coordinate3D[][][]
    | null {
    if (!this.geometry) return null;

    const coordinateMapping: Record<
      Geometry["type"],
      Coordinate3D | Coordinate3D[] | Coordinate3D[][] | Coordinate3D[][][]
    > = {
      GeometryCollection: null, // Not supported
      LineString: this.geometry.coordinates as Coordinate3D[],
      MultiLineString: this.geometry.coordinates as Coordinate3D[][],
      MultiPoint: this.geometry.coordinates as Coordinate3D[],
      MultiPolygon: this.geometry.coordinates as Coordinate3D[][][],
      Point: this.geometry.coordinates as Coordinate3D,
      Polygon: this.geometry.coordinates as Coordinate3D[][],
    };

    return coordinateMapping[this.geometry.type] ?? null;
  }

  public setCoordinates(
    coordinates:
      | Coordinate3D
      | Coordinate3D[]
      | Coordinate3D[][]
      | Coordinate3D[][][],
  ): void {
    if (!this.geometry) return;

    const validTypes: Record<
      Geometry["type"],
      Coordinate3D | Coordinate3D[] | Coordinate3D[][] | Coordinate3D[][][]
    > = {
      GeometryCollection: null, // Not supported, as it contains multiple geometries
      LineString: coordinates as Coordinate3D[],
      MultiLineString: coordinates as Coordinate3D[][],
      MultiPoint: coordinates as Coordinate3D[],
      MultiPolygon: coordinates as Coordinate3D[][][],
      Point: coordinates as Coordinate3D,
      Polygon: coordinates as Coordinate3D[][],
    };

    if (this.geometry.type in validTypes) {
      this.geometry.coordinates = validTypes[this.geometry.type]!;
    } else {
      throw new Error(`Invalid geometry type: ${this.geometry.type}`);
    }
  }

  public setProperty<K extends keyof P>(key: K, value: P[K]): void {
    if (value == null || value == undefined) {
      delete this.properties[key];
      return;
    }
    this.properties[key] = value;
  }

  public addProperty<K extends string, V>(key: K, value: V): void {
    (this.properties as Record<string, any>)[key] = value;
  }

  public toGeoJson() {
    return {
      geometry: { ...this.geometry },
      id: this.id,
      properties: { ...this.properties },
      type: this.type,
    } as Feature<G, P>;
  }

  public getProperty(property: string) {
    const propSnake = property;
    const propCamel = snakeToCamelCase(property);
    const properties = this.properties as GeoJsonProperties;
    return properties[propSnake] ?? properties[propCamel];
  }
}

export default IMDFFeature;
